// Helpers for showing a human-readable attachment name.
//
// Uploads are stored under a key that prefixes the original filename with a
// uuid (S3) or a timestamp (local dev) so two people uploading "brochure.pdf"
// don't overwrite each other. That prefix is storage plumbing and should never
// have been shown to users.
//
// Posts created from now on carry the original name in `attachment_name`.
// For posts created before that field existed there is nothing to read, so we
// recover the name by stripping the known prefix shapes off the URL basename.

// "9f8c1e40-3b2a-4c7d-9e11-2ab4c6d8e0f1_Brand_Deck.pdf"
const UUID_PREFIX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i;
// "20260731_063102_Brand_Deck.pdf"
const TIMESTAMP_PREFIX = /^\d{8}_\d{6}_/;

/** Last path segment of a URL, query string and encoding removed. */
export function basenameFromUrl(url) {
  if (!url) return '';
  const path = String(url).split('?')[0].split('#')[0];
  const raw = path.split('/').pop() || '';
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

/** Remove the storage-generated uniqueness prefix from a stored filename. */
export function stripStoragePrefix(name) {
  if (!name) return '';
  return name.replace(UUID_PREFIX, '').replace(TIMESTAMP_PREFIX, '');
}

/**
 * Best available display name for a post attachment.
 *
 * Prefers the stored original filename, falls back to de-prefixing the URL for
 * older posts, and finally to a generic label so the UI never renders an empty
 * chip.
 */
export function attachmentDisplayName(post, fallback = 'Attachment') {
  const stored = (post?.attachment_name || '').trim();
  if (stored) return stored;

  const derived = stripStoragePrefix(basenameFromUrl(post?.attachment_url));
  return derived || fallback;
}
